import { useMemo } from 'react';

import { IContentTypeValue } from 'graphql/types';
import { TFilterFormValues } from 'common/types';
import { useUrlFilterParams } from 'common/hooks/useUrlFilterParams';
import {
    filtersToBase64String,
    filtersToQueryParams,
} from 'common/utils/filter';
import { useExtraCategoryFilters } from 'common/hooks/useExtraCategoryFilters';

export const useCertificateFilter = (filterParamsInUrl = true) => {
    const { filterParams, setFilterParam } = useUrlFilterParams(
        'certificate',
        filterParamsInUrl
    );

    const { loading, filters } = useExtraCategoryFilters(
        IContentTypeValue.Certificate
    );

    const searchQueryParam = filterParams.q || '';
    const selectedFilterParams = filterParams.filters;

    const handleSearch = (searchValue: string) => {
        setFilterParam('q', searchValue);
    };

    const handleFilterChange = (values?: TFilterFormValues) => {
        const filterString = filtersToBase64String(filters, values);

        setFilterParam('filters', filterString);
    };

    const selectedFilters: TFilterFormValues = useMemo(() => {
        if (!selectedFilterParams) return {};

        try {
            return JSON.parse(window.atob(selectedFilterParams));
        } catch {
            return {};
        }
    }, [selectedFilterParams]);

    // Parse selected filters into the shape expected by the certificates query.
    // `filters` must be a dependency: it starts empty while useExtraCategoryFilters
    // loads, so a URL-provided filter would otherwise be dropped from the query
    // when the filters resolve after selectedFilters is already set.
    const filterValuesForQuery = useMemo(
        () => filtersToQueryParams<TFilterFormValues>(filters, selectedFilters),
        [filters, selectedFilters]
    );

    return {
        filters,
        filterValues: { ...selectedFilters, q: searchQueryParam },
        filterValuesForQuery: { ...filterValuesForQuery, q: searchQueryParam },
        handleSearch,
        handleFilterChange,
        loading,
    };
};
